"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  getDisplayImageUrl,
  getSyncDisplayUrlHint,
  shouldBypassNextImageOptimizer,
} from "@/lib/heic-images";

/** Один загружаемый кадр: смена `src` через `key` сбрасывает появление. */
function CarouselSlideDecoded({
  src,
  alt,
  sizes,
  eager,
}: {
  src: string;
  alt: string;
  sizes: string;
  eager: boolean;
}) {
  const [ready, setReady] = useState(false);

  const fadeRaster = cn(
    "object-cover motion-safe:transition-[opacity,transform] motion-safe:duration-300 motion-safe:ease-out",
    "motion-safe:group-hover/car-scale:scale-105 motion-reduce:group-hover/car-scale:scale-100",
    ready ? "opacity-100" : "opacity-0 motion-reduce:opacity-100",
  );

  return (
    <>
      {!ready ? (
        <div
          className="absolute inset-0 z-[1] animate-pulse bg-gradient-to-br from-gray-100 to-gray-200/90 dark:from-gray-800 dark:to-gray-900"
          aria-hidden
        >
          <div className="absolute inset-0 flex items-center justify-center opacity-25">
            <ImageIcon className="h-10 w-10 text-gray-400 dark:text-gray-600" strokeWidth={1} aria-hidden />
          </div>
        </div>
      ) : null}
      <div className="relative z-[2] h-full w-full">
        {src.startsWith("blob:") ? (
          // eslint-disable-next-line @next/next/no-img-element -- blob после HEIC
          <img
            src={src}
            alt={alt}
            className={cn("h-full w-full", fadeRaster)}
            draggable={false}
            decoding="async"
            loading={eager ? "eager" : "lazy"}
            onLoad={() => setReady(true)}
            onError={() => setReady(true)}
          />
        ) : (
          <Image
            src={src}
            alt={alt}
            fill
            sizes={sizes}
            quality={72}
            unoptimized={shouldBypassNextImageOptimizer(src)}
            className={cn(fadeRaster, "object-cover")}
            draggable={false}
            preload={eager}
            decoding="async"
            {...(eager ? ({ loading: "eager" as const, fetchPriority: "high" as const } as const) : {})}
            onLoad={() => setReady(true)}
            onError={() => setReady(true)}
          />
        )}
      </div>
    </>
  );
}

function CarouselSlideImage({ raw, alt, sizes, eager }: { raw: string; alt: string; sizes: string; eager: boolean }) {
  const [displaySrc, setDisplaySrc] = useState(() => getSyncDisplayUrlHint(raw));

  useEffect(() => {
    let alive = true;
    getDisplayImageUrl(raw).then((d) => {
      if (alive) setDisplaySrc(d);
    });
    return () => {
      alive = false;
    };
  }, [raw]);

  if (!displaySrc) {
    return (
      <div className="relative h-full w-full" aria-busy="true">
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-100 to-gray-200/90 dark:from-gray-800 dark:to-gray-900" aria-hidden />
        <div className="absolute inset-0 flex items-center justify-center opacity-30">
          <ImageIcon className="h-10 w-10 text-gray-400 dark:text-gray-600" strokeWidth={1} aria-hidden />
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <CarouselSlideDecoded key={displaySrc} src={displaySrc} alt={alt} sizes={sizes} eager={eager} />
    </div>
  );
}

type Props = {
  urls: string[];
  alt: string;
  sizes: string;
  imagePriority?: boolean;
  /** Tap по фото (не swipe, не стрелки) — переход в объявление */
  onImageTap?: () => void;
};

const SWIPE_PX = 52;

export default function CarCardImageCarousel({ urls, alt, sizes, imagePriority, onImageTap }: Props) {
  const t = useTranslations("CarCard");
  const list = useMemo(() => urls.filter(Boolean), [urls]);
  const n = list.length;
  const [i, setI] = useState(0);
  const viewIndex = n === 0 ? 0 : Math.min(Math.max(0, i), n - 1);
  const raw = list[viewIndex] ?? "";
  const eager = !!(imagePriority ?? false);

  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const suppressNextClickNav = useRef(false);

  const go = useCallback(
    (delta: number) => {
      if (n <= 1) return;
      setI((prev) => (prev + delta + n) % n);
    },
    [n],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start || n <= 1) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.25) {
      e.preventDefault();
      e.stopPropagation();
      suppressNextClickNav.current = true;
      if (dx > 0) go(-1);
      else go(1);
    }
  };

  if (n < 1) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-gray-300 dark:bg-gray-900 dark:text-gray-600">
        <ImageIcon size={52} strokeWidth={1} className="opacity-40" aria-hidden />
      </div>
    );
  }

  return (
    <div
      className="group/car-scale absolute inset-0"
      data-carousel="car-card"
      role="region"
      aria-label={n > 1 ? t("carouselAria", { title: alt, total: n }) : alt}
    >
      {/* Swipe / tap по фото; стрелки и точки — z-[20], выше этого слоя */}
      {n > 1 ? (
        <div
          role="presentation"
          tabIndex={-1}
          className="absolute inset-0 z-[5] touch-pan-y select-none"
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (suppressNextClickNav.current) {
              suppressNextClickNav.current = false;
              return;
            }
            onImageTap?.();
          }}
          style={{ touchAction: "pan-y" }}
        />
      ) : (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          className="absolute inset-0 z-[5] cursor-pointer border-0 bg-transparent p-0"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onImageTap?.();
          }}
        />
      )}

      <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden rounded-t-[inherit]">
        <div className="relative h-full w-full">
          <CarouselSlideImage key={raw} raw={raw} alt={alt} sizes={sizes} eager={eager} />
        </div>
      </div>

      {n > 1 ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-2 z-[20] flex max-w-full justify-center gap-1.5 overflow-hidden px-9">
            {list.map((u, idx) => (
              <button
                key={`${u}-${idx}`}
                type="button"
                className={cn(
                  "pointer-events-auto relative z-[20] h-1.5 shrink-0 rounded-full motion-safe:transition-all motion-safe:duration-200",
                  idx === viewIndex ? "w-5 bg-[#E63946]" : "w-1.5 bg-white/90 ring-1 ring-black/15 hover:bg-white dark:bg-gray-700/95 dark:ring-white/20 dark:hover:bg-gray-700",
                )}
                aria-label={t("carouselPhoto", { n: idx + 1 })}
                aria-current={idx === viewIndex ? true : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  suppressNextClickNav.current = false;
                  setI(idx);
                }}
              />
            ))}
          </div>
          <button
            type="button"
            className="pointer-events-auto absolute left-1 top-1/2 z-[20] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/95 text-gray-900 shadow-lg backdrop-blur-sm motion-safe:transition-transform motion-safe:hover:scale-105 motion-safe:active:scale-95 dark:border-white/10 dark:bg-gray-900/95 dark:text-gray-50"
            aria-label={t("carouselPrev")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              suppressNextClickNav.current = false;
              go(-1);
            }}
          >
            <ChevronLeft size={18} aria-hidden strokeWidth={2.25} />
          </button>
          <button
            type="button"
            className="pointer-events-auto absolute right-1 top-1/2 z-[20] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/95 text-gray-900 shadow-lg backdrop-blur-sm motion-safe:transition-transform motion-safe:hover:scale-105 motion-safe:active:scale-95 dark:border-white/10 dark:bg-gray-900/95 dark:text-gray-50"
            aria-label={t("carouselNext")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              suppressNextClickNav.current = false;
              go(1);
            }}
          >
            <ChevronRight size={18} aria-hidden strokeWidth={2.25} />
          </button>
        </>
      ) : null}
    </div>
  );
}
